import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { downloadBlob, shiftColours, CELL_NAMES } from "@/components/roster/shiftColours";
import { formatDateTimeIST } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, FileSpreadsheet, FileText, Search } from "lucide-react";
import { toast } from "sonner";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const ALL = "__all__";

function Pick({ value, onChange, items, label, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-full sm:w-52" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All {label}</SelectItem>
        {items.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function Legend({ shifts, colours }) {
  const items = [...shifts.map((s) => [s.code, `${s.name} ${s.start || ""}–${s.end || ""}`]), ...Object.entries(CELL_NAMES)];
  return (
    <div className="flex flex-wrap gap-2 text-xs" data-testid="combined-legend">
      {items.map(([c, n], i) => (
        <span key={`${c}-${i}`} className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1">
          <b className="rounded px-1.5" style={{ background: colours[c] }}>{c}</b><span className="text-slate-700">{n}</span>
        </span>
      ))}
    </div>
  );
}

function Grid({ rows, dates, colours }) {
  let group = null;
  return (
    <div className="max-h-[70vh] overflow-auto rounded-lg border border-slate-200" data-testid="combined-grid">
      <table className="min-w-max border-collapse text-xs">
        <thead className="sticky top-0 z-20 bg-[#0B1A4B] text-white">
          <tr>
            <th className="sticky left-0 z-30 min-w-[130px] bg-[#0B1A4B] sm:min-w-[200px] px-3 py-2 text-left">Employee</th>
            {dates.map((d) => <th key={d} className="w-10 px-1 py-1 text-center font-semibold">{d.slice(8)}<br /><span className="font-normal opacity-70">{DAYS[new Date(`${d}T00:00`).getDay()]}</span></th>)}
          </tr>
        </thead>
        <tbody>
          {rows.flatMap((r) => {
            const g = r.location + (r.department ? ` · ${r.department}` : "");
            const out = [];
            if (g !== group) {
              group = g;
              out.push(<tr key={`g-${g}`} className="bg-indigo-50"><td colSpan={dates.length + 1} className="px-3 py-1.5 text-sm font-bold text-indigo-950" data-testid="combined-group"><span className="sticky left-3">{g}</span></td></tr>);
            }
            out.push(
              <tr key={`${g}-${r.employee_code}`} className="border-t border-slate-100" data-testid={`combined-row-${r.employee_code}`}>
                <td className="sticky left-0 z-10 bg-white px-3 py-1.5"><p className="font-semibold text-slate-900">{r.name || "—"}</p><p className="text-slate-500">{r.employee_code}</p></td>
                {dates.map((d) => { const v = r.cells[d] || ""; return <td key={d} className="border-l border-slate-100 text-center font-semibold" style={{ background: colours[v] }}>{v}</td>; })}
              </tr>
            );
            return out;
          })}
        </tbody>
      </table>
      {!rows.length && <p className="p-6 text-sm text-slate-500" data-testid="combined-empty">No employees match these filters.</p>}
    </div>
  );
}

export default function FinalRosterView() {
  const { fid } = useParams();
  const nav = useNavigate();
  const [loc, setLoc] = useState(ALL);
  const [dept, setDept] = useState(ALL);
  const [q, setQ] = useState("");
  const { data, isLoading, error } = useQuery({ queryKey: ["final-view", fid], queryFn: async () => (await api.get(`/roster/final/${fid}`)).data });
  const colours = useMemo(() => shiftColours(data?.shifts || []), [data]);
  const rows = useMemo(() => (data?.rows || []).filter((r) => (loc === ALL || r.location === loc) && (dept === ALL || r.department === dept)
    && (!q || `${r.name} ${r.employee_code}`.toLowerCase().includes(q.toLowerCase()))), [data, loc, dept, q]);
  if (isLoading) return <p className="p-6 text-sm text-slate-500">Loading…</p>;
  if (error) return <p className="p-6 text-sm text-rose-700" data-testid="combined-error">{apiError(error.response?.data?.detail)}</p>;
  const uniq = (k) => [...new Set(data.rows.map((r) => r[k]).filter(Boolean))].sort();
  const params = new URLSearchParams({ ...(loc !== ALL && { location: loc }), ...(dept !== ALL && { department: dept }) }).toString();
  const base = `final_roster_${data.year}_${String(data.month).padStart(2, "0")}_v${data.version}`;
  const dl = (ext) => downloadBlob(api, `/roster/final/${fid}/export.${ext}${params ? `?${params}` : ""}`, `${base}.${ext}`).catch(() => toast.error("Download failed"));
  return (
    <div data-testid="combined-roster-page">
      <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={() => (window.history.state?.idx ? nav(-1) : nav("/"))} data-testid="combined-back-btn"><ArrowLeft className="mr-1 h-4 w-4" /> Back</Button>
      <PageHeader title={`${data.org_name} — ${MONTHS[data.month - 1]} ${data.year}`}
        description={`Combined roster v${data.version} · ${data.roster_count} approved roster(s) · ${data.employee_count} employees · sent by ${data.sent_by} on ${formatDateTimeIST(data.sent_at)}`} />
      <Card className="space-y-4 p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
            <Pick value={loc} onChange={setLoc} items={uniq("location")} label="locations" testid="combined-location-filter" />
            <Pick value={dept} onChange={setDept} items={uniq("department")} label="departments" testid="combined-department-filter" />
            <div className="relative"><Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search employee" className="w-full pl-8 sm:w-56" data-testid="combined-search" /></div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => dl("xlsx")} data-testid="combined-export-xlsx"><FileSpreadsheet className="mr-1 h-4 w-4" /> Excel</Button>
            <Button variant="outline" size="sm" onClick={() => dl("pdf")} data-testid="combined-export-pdf"><FileText className="mr-1 h-4 w-4" /> PDF</Button>
          </div>
        </div>
        <p className="text-sm text-slate-600" data-testid="combined-count">Showing {rows.length} of {data.rows.length} employees{q ? " (exports ignore the search box)" : ""}</p>
        <Grid rows={rows} dates={data.dates} colours={colours} />
        <Legend shifts={data.shifts} colours={colours} />
      </Card>
    </div>
  );
}
